import { apiRequest } from './axiosConfig';
import { 
  AuthResponse, 
  User, 
  ForgotPasswordRequest,
  ResetPasswordRequest,
  PasswordResetResponse,
  PasswordStatusResponse,
  SetPasswordRequest,
  SetPasswordResponse,
  McpToken,
  McpTokenListResponse,
  McpTokenCreateRequest,
  McpTokenCreateResponse,
} from '../../types';
import { InternalAxiosRequestConfig } from 'axios';
import tokenManager from '../../utils/tokenManager';
import { logger } from '../../utils/logger';
import { getAdClickIds, reserveEventId } from '../../utils/metaPixel';
import {
  PARTNER_REF_REGEX,
  PARTNER_REF_STORAGE_KEY,
  PARTNER_TRIAL_DAYS_STORAGE_KEY,
  parsePartnerTrialDays,
} from '../../constants/partnerRef';

// リトライフラグを持つリクエスト設定の拡張型
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
  remember?: boolean;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

/**
 * 招待URL経由の帰属を Firebase 認証リクエストへ載せる。
 *
 * 紹介コードは /signup で localStorage に退避され、これまでオンボーディング完了時にしか
 * サーバーへ送られなかった。アカウント作成とオンボーディングが別端末になると帰属が
 * 失われるため、アカウントが初めてサーバー上に生まれる Firebase 認証時にも同送する。
 * 保存するかどうか（既存利用者は対象外・最初のコードを優先）はサーバー側で判断する。
 */
const partnerAttributionPayload = (): Partial<FirebaseAuthRequest> => {
  try {
    const partnerRef = localStorage.getItem(PARTNER_REF_STORAGE_KEY);
    if (!partnerRef || !PARTNER_REF_REGEX.test(partnerRef)) return {};
    const trialDays = parsePartnerTrialDays(
      localStorage.getItem(PARTNER_TRIAL_DAYS_STORAGE_KEY),
    );
    return trialDays
      ? { partner_ref: partnerRef, partner_trial_days: trialDays }
      : { partner_ref: partnerRef };
  } catch {
    // プライベートブラウジング等で localStorage が使えない場合は帰属なしで続行する。
    return {};
  }
};

export interface FirebaseAuthRequest {
  firebase_id_token: string;
  name: string;
  email: string;
  provider: string;
  provider_user_id: string;
  photo_url?: string | null;
  // 招待URL(/signup?partner_ref=CODE&trial_days=14)由来の帰属。
  // loginWithFirebase が localStorage から自動で載せるため、呼び出し側は指定しない。
  partner_ref?: string;
  partner_trial_days?: number;
  // 広告のクリック識別子と、ブラウザ側 fbq に渡す eventID。
  // partner_ref と同じく loginWithFirebase が自動で載せるため、呼び出し側は指定しない。
  fbc?: string;
  fbp?: string;
  twclid?: string;
  event_id?: string;
}

/**
 * 広告のクリック識別子と event_id を組み立てる。
 *
 * 実際の新規登録はこの Firebase 経路を通る。サーバー（AuthController#firebase）が
 * Conversions API から同じ event_id で CompleteRegistration を撃つので、
 * ブラウザ側の fbq が広告ブロッカー等で落ちても成果が欠けない
 * （両方届いた場合は Meta 側で1件に丸められる）。
 */
const adAttributionPayload = (): Record<string, string> => {
  const payload: Record<string, string> = {};
  try {
    const ids = getAdClickIds();
    if (ids.fbc) payload.fbc = ids.fbc;
    if (ids.fbp) payload.fbp = ids.fbp;
    if (ids.twclid) payload.twclid = ids.twclid;
    // 予約した id は、このあと SignupPage / CompleteSignInPage が
    // trackMetaCompleteRegistration を撃つときに消費される。
    payload.event_id = reserveEventId('CompleteRegistration');
  } catch {
    // 計測は認証を絶対に止めない
  }
  return payload;
};

class AuthService {
  /**
   * ログイン処理
   * @param credentials ログイン情報（メール、パスワード）
   * @returns 認証レスポンス（トークン、ユーザー情報）
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      // 環境変数からAPIのURLを取得
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      logger.log('Using backend URL:', backendUrl);
      logger.log('Sending login request to API');
      
      const response = await apiRequest.post<AuthResponse>('auth/login', credentials);
      
      // 生のレスポンスデータをログに出力
      logger.log('Login 生レスポンス:', JSON.stringify(response, null, 2));
      logger.log('Login response 詳細:', {
        hasToken: !!response.token,
        tokenLength: response?.token?.length,
        hasRefreshToken: !!response.refresh_token,
        refreshTokenLength: response?.refresh_token?.length,
        hasUser: !!response.user
      });
      
      // トークンマネージャーを使用してトークンを保存
      if (!response.token) {
        console.warn('No token received in login response');
        throw new Error('アクセストークンがAPI応答に含まれていません'); // アクセストークンがない場合はログイン失敗
      }
      tokenManager.saveToken(response.token);
      logger.log('アクセストークンを保存しました', {
        tokenLength: response.token.length
      });
      
      logger.log('DEBUG: About to check refresh_token existence.');
      logger.log('Login response refresh_token value:', response.refresh_token);

      // リフレッシュトークンの保存前に確認ログを追加
      if (!response.refresh_token) {
        console.warn('No refresh token received in login response');
        throw new Error('リフレッシュトークンがAPI応答に含まれていません'); // リフレッシュトークンがない場合はログイン失敗
      }
      
      logger.log('リフレッシュトークン保存開始 - 長さ:', response.refresh_token.length);
      
      // 保存前の状態を確認
      const beforeSaveCheck = tokenManager.getRefreshToken();
      logger.log('保存前のリフレッシュトークン状態:', {
        exists: !!beforeSaveCheck,
        length: beforeSaveCheck?.length
      });
      
      // リフレッシュトークンを保存
      tokenManager.saveRefreshToken(response.refresh_token);
      
      // 確実に保存されたことを確認
      const afterSaveCheck = tokenManager.getRefreshToken();
      logger.log('リフレッシュトークン保存後の状態:', {
        exists: !!afterSaveCheck,
        length: afterSaveCheck?.length,
        isSame: afterSaveCheck === response.refresh_token
      });
      
      // リフレッシュトークンが正しく保存されていない場合は、もう一度試みる
      if (!afterSaveCheck || afterSaveCheck !== response.refresh_token) {
        console.warn('リフレッシュトークンが正しく保存されていません。再試行します。');
        tokenManager.saveRefreshToken(response.refresh_token);
        
        // 再度確認
        const retryCheck = tokenManager.getRefreshToken();
        logger.log('リフレッシュトークン再保存後の状態:', {
          exists: !!retryCheck,
          length: retryCheck?.length,
          isSame: retryCheck === response.refresh_token
        });
        
        // それでも保存されていない場合はエラー
        if (!retryCheck) {
          throw new Error('リフレッシュトークンの保存に失敗しました');
        }
      }
      
      logger.log('リフレッシュトークンを保存しました', {
        refreshTokenLength: response.refresh_token.length
      });
      
      if (response.user) {
        tokenManager.saveUserData(response.user);
      }
      
      // 保存後の状態を確認
      const savedToken = tokenManager.getToken();
      const savedRefreshToken = tokenManager.getRefreshToken();
      
      logger.log('保存後のトークン状態:', {
        hasToken: !!savedToken,
        tokenLength: savedToken?.length,
        hasRefreshToken: !!savedRefreshToken,
        refreshTokenLength: savedRefreshToken?.length
      });
      
      // リフレッシュトークンが保存されていない場合はエラー
      if (!savedRefreshToken) {
        console.error('致命的エラー: リフレッシュトークンの保存に失敗しました。');
        throw new Error('リフレッシュトークンの保存に失敗しました。再度ログインしてください。');
      }
      
      // 少し待機してからレスポンスを返す（ストレージの反映を待つため）
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // 最終確認
      const finalRefreshToken = tokenManager.getRefreshToken();
      logger.log('最終確認 - リフレッシュトークン状態:', {
        exists: !!finalRefreshToken,
        length: finalRefreshToken?.length
      });
      
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }
  
  /**
   * 新規登録処理
   * @param userData 登録ユーザー情報
   * @returns 認証レスポンス（トークン、ユーザー情報）
   */
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      // 環境変数からAPIのURLを取得
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      logger.log('Using backend URL:', backendUrl);
      logger.log('Sending register request to API');
      
      // 登録APIを呼び出し
      const response = await apiRequest.post<AuthResponse>('auth/register', userData);
      logger.log('Register response:', response);
      
      // トークンマネージャーを使用してトークンを保存
      if (!response.token) {
        console.warn('No token received in register response');
        throw new Error('アクセストークンがAPI応答に含まれていません');
      }
      tokenManager.saveToken(response.token);
      
      if (!response.refresh_token) {
        console.warn('No refresh token received in register response');
        throw new Error('リフレッシュトークンがAPI応答に含まれていません');
      }
      tokenManager.saveRefreshToken(response.refresh_token);
      
      if (response.user) {
        tokenManager.saveUserData(response.user);
      }
      
      return response;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  }
  
  /**
   * ログアウト処理
   * @returns 成功したかどうか
   */
  async logout(): Promise<void> {
    logger.log('=== authService.logout()が呼び出されました ===');
    try {
      // 環境変数からAPIのURLを取得
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      logger.log('Using backend URL:', backendUrl);
      logger.log('Sending logout request to API...');
      
      // ログアウトAPIを呼び出し
      const response = await apiRequest.post<{ message: string }>('auth/logout');
      logger.log('ログアウトAPIレスポンス:', response);
      
      // トークンマネージャーを使用してすべての認証データをクリア
      logger.log('トークンマネージャーで認証データをクリアします...');
      tokenManager.clearAllAuthData();
      logger.log('認証データのクリア完了');
    } catch (error) {
      console.error('Logout failed - 詳細:', error);
      console.error('エラーレスポンス:', error?.response);
      // エラーが発生してもすべての認証データをクリア
      logger.log('エラーが発生しましたが、認証データをクリアします');
      tokenManager.clearAllAuthData();
      throw error;
    }
  }
  
  /**
   * 管理者ログイン処理
   * @param credentials ログイン情報（メール、パスワード）
   * @returns 認証レスポンス（トークン、管理者情報）
   */
  async adminLogin(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      // 環境変数からAPIのURLを取得
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      logger.log('Using backend URL:', backendUrl);
      logger.log('Sending admin login request to API');
      
      const response = await apiRequest.post<AuthResponse>('auth/admin/login', credentials);
      logger.log('Admin login response:', response);
      
      // トークンマネージャーを使用してトークンを保存
      if (!response.token) {
        console.warn('No token received in admin login response');
        throw new Error('アクセストークンがAPI応答に含まれていません');
      }
      tokenManager.saveToken(response.token);
      
      // 管理者情報も保存
      if (response.admin) {
        tokenManager.saveUserData(response.admin);
      }
      
      return response;
    } catch (error) {
      console.error('Admin login failed:', error);
      throw error;
    }
  }

  /**
   * 現在のユーザー情報を取得
   * @returns ユーザー情報
   */
  async getCurrentUser(): Promise<User> {
    try {
      // トークンマネージャーからトークンを取得
      const token = tokenManager.getToken();
      if (!token) {
        console.warn('getCurrentUser: No token available');
        throw new Error('認証トークンがありません');
      }
      
      // リフレッシュトークンの存在も確認
      const refreshToken = tokenManager.getRefreshToken();
      logger.log('getCurrentUser: Token exists', {
        tokenLength: token.length, 
        hasRefreshToken: !!refreshToken 
      });
      
      // 環境変数からAPIのURLを取得
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      logger.log('Using backend URL:', backendUrl);
      logger.log('Fetching current user data');
      
      // ユーザープロフィールAPIを呼び出し
      try {
        const response = await apiRequest.get<{ user: User }>('users/profile');
        logger.log('User data received:', response);
        // Profile API returns {user: {...}} - unwrap to get the User object
        const userData = response.user || response;
        if (userData) {
          tokenManager.saveUserData(userData);
        }
        return userData as User;
      } catch (error: any) {
        console.error('API call to fetch user profile failed:', error);
        
        // エラーの詳細をログ
        if (error.response) {
          console.error('API error details:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          });
        }
        
        // 401 は axios interceptor が既にリフレッシュ→再試行している（error.config._retry）。
        // ここで再度リフレッシュすると回転が二重に走るだけなので、interceptor を通らなかった
        // 401 に限ってリフレッシュを試みる。ネットワークエラー・5xx ではリフレッシュしない。
        if (refreshToken && error?.response?.status === 401 && !error?.config?._retry) {
          logger.log('getCurrentUser: Attempting to refresh token before retry');
          const refreshResult = await this.refreshToken();
          
          if (refreshResult && refreshResult.token) {
            logger.log('getCurrentUser: Token refreshed, retrying profile fetch');
            const retryResponse = await apiRequest.get<{ user: User }>('users/profile');
            logger.log('User data received after token refresh:', retryResponse);

            // Profile API returns {user: {...}} - unwrap to get the User object
            const retryUserData = (retryResponse as any).user || retryResponse;

            // ユーザーデータを保存
            if (retryUserData) {
              tokenManager.saveUserData(retryUserData);
            }

            return retryUserData as User;
          }
        }
        
        throw error;
      }
    } catch (error) {
      console.error('Failed to get current user:', error);
      throw error;
    }
  }
  
  /**
   * トークンの有効性チェック
   * @returns トークンが有効かどうか
   */
  async checkToken(): Promise<boolean> {
    try {
      // トークンマネージャーからトークンを取得
      const token = tokenManager.getToken();
      if (!token) {
        logger.log('checkToken: No token available');
        return false;
      }
      
      logger.log('checkToken: Validating token');
      
      try {
        // 現在のユーザー情報が取得できれば、トークンは有効
        await this.getCurrentUser();
        logger.log('checkToken: Token is valid');
        return true;
      } catch (error) {
        // ユーザー情報取得エラー
        console.warn('checkToken: Token validation failed');
        
        // リフレッシュトークンがあれば試行
        const refreshToken = tokenManager.getRefreshToken();
        if (refreshToken) {
          logger.log('checkToken: Attempting to refresh token');
          try {
            const refreshResult = await this.refreshToken();
            // リフレッシュ成功
            if (refreshResult && refreshResult.token) {
              logger.log('checkToken: Token refreshed successfully');
              return true;
            }
          } catch (refreshError) {
            console.error('checkToken: Token refresh failed', refreshError);
          }
        }
        
        return false;
      }
    } catch (error) {
      console.error('checkToken: Unexpected error', error);
      return false;
    }
  }
  
  /**
   * パスワードリセットリンクを送信する処理
   * @param data パスワードリセット要求（メールアドレス）
   * @returns レスポンス（メッセージ、ステータス）
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<PasswordResetResponse> {
    try {
      // 環境変数からAPIのURLを取得
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      logger.log('Using backend URL:', backendUrl);
      logger.log('Sending forgot password request');
      
      // パスワードリセットメール送信APIを呼び出し
      const response = await apiRequest.post<PasswordResetResponse>('auth/forgot-password', data);
      logger.log('Forgot password response:', response);
      
      return response;
    } catch (error) {
      console.error('Forgot password request failed:', error);
      throw error;
    }
  }
  
  /**
   * パスワードリセット処理
   * @param data パスワードリセットデータ（トークン、メール、新パスワード）
   * @returns レスポンス（メッセージ、ステータス）
   */
  async resetPassword(data: ResetPasswordRequest): Promise<PasswordResetResponse> {
    try {
      // 環境変数からAPIのURLを取得
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      logger.log('Using backend URL:', backendUrl);
      logger.log('Sending reset password request');
      
      // パスワードリセットAPIを呼び出し
      const response = await apiRequest.post<PasswordResetResponse>('auth/reset-password', data);
      logger.log('Reset password response:', response);
      
      return response;
    } catch (error) {
      console.error('Reset password request failed:', error);
      throw error;
    }
  }
  
  /**
   * 現在のログイン方法とパスワード設定状況を取得する
   * @returns パスワード設定状況（has_password / providers）
   */
  async getPasswordStatus(): Promise<PasswordStatusResponse> {
    return apiRequest.get<PasswordStatusResponse>('users/password-status');
  }

  /**
   * ログインしたままパスワードを設定・変更する
   *
   * Google ログインのみのユーザーは current_password を省略できる
   * （設定済みのユーザーはサーバー側で必須になる）
   * @param data 新しいパスワード
   * @returns 設定結果
   */
  async setPassword(data: SetPasswordRequest): Promise<SetPasswordResponse> {
    const response = await apiRequest.post<SetPasswordResponse>('users/password', data);
    // パスワード変更でサーバーは全端末のリフレッシュトークンを失効させるので、
    // この端末用に発行し直されたものへ差し替える（無いと 24 時間後にログアウトされる）
    if (response.refresh_token) {
      tokenManager.saveRefreshToken(response.refresh_token);
    }
    return response;
  }

  /**
   * MCP用アクセストークンの一覧を取得する
   *
   * Googleログインのユーザーはパスワードを持たないためMCPにログインできない。
   * このトークンが認証方法によらない共通の入口になる。
   */
  async listMcpTokens(): Promise<McpToken[]> {
    const response = await apiRequest.get<McpTokenListResponse>('users/mcp-tokens');
    return response.data;
  }

  /**
   * MCP用アクセストークンを発行する
   *
   * 平文トークンはこのレスポンスにしか現れない（再表示不可）。
   * @param data ラベルと有効期限（日数・省略時90日）
   */
  async createMcpToken(data: McpTokenCreateRequest): Promise<McpTokenCreateResponse> {
    return apiRequest.post<McpTokenCreateResponse>('users/mcp-tokens', data);
  }

  /**
   * MCP用アクセストークンを失効させる
   */
  async revokeMcpToken(tokenId: number): Promise<void> {
    await apiRequest.delete(`users/mcp-tokens/${tokenId}`);
  }

  /**
   * Firebase認証によるログイン
   * @param authData Firebase認証データ
   * @returns 認証レスポンス
   */
  async loginWithFirebase(authData: FirebaseAuthRequest): Promise<AuthResponse> {
    try {
      // 環境変数からAPIのURLを取得
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      logger.log('Using backend URL:', backendUrl);
      logger.log('Sending Firebase auth request');
      
      // Firebase認証APIを呼び出し。招待帰属を同送してサーバー側に控えさせる。
      const response = await apiRequest.post<AuthResponse>('auth/firebase', {
        ...authData,
        ...partnerAttributionPayload(),
        ...adAttributionPayload(),
      });
      logger.log('Firebase auth response:', response);
      
      // トークンマネージャーを使用してトークンを保存
      if (!response.token) {
        console.warn('No token received in Firebase auth response');
        throw new Error('アクセストークンがAPI応答に含まれていません');
      }
      // 保存前の状態を確認
      logger.log('トークン保存前の状態:', {
        token: !!response.token,
        tokenLength: response.token?.length,
        refreshToken: !!response.refresh_token,
        refreshTokenLength: response.refresh_token?.length
      });
      
      tokenManager.saveToken(response.token);
      
      // Firebase認証ではリフレッシュトークンが返されない場合の対処
      if (!response.refresh_token) {
        console.warn('リフレッシュトークンがレスポンスに含まれていません');
        throw new Error('リフレッシュトークンがAPI応答に含まれていません');
      }
      logger.log('リフレッシュトークン保存開始 - 長さ:', response.refresh_token.length);
      
      // 保存前の状態を確認
      const beforeSaveCheck = tokenManager.getRefreshToken();
      logger.log('保存前のリフレッシュトークン状態:', {
        exists: !!beforeSaveCheck,
        length: beforeSaveCheck?.length
      });
      
      // リフレッシュトークンを保存
      tokenManager.saveRefreshToken(response.refresh_token);
      
      // 確実に保存されたことを確認
      const afterSaveCheck = tokenManager.getRefreshToken();
      logger.log('リフレッシュトークン保存後の状態:', {
        exists: !!afterSaveCheck,
        length: afterSaveCheck?.length,
        isSame: afterSaveCheck === response.refresh_token
      });
      
      // リフレッシュトークンが正しく保存されていない場合は、もう一度試みる
      if (!afterSaveCheck || afterSaveCheck !== response.refresh_token) {
        console.warn('リフレッシュトークンが正しく保存されていません。再試行します。');
        tokenManager.saveRefreshToken(response.refresh_token);
        
        // 再度確認
        const retryCheck = tokenManager.getRefreshToken();
        logger.log('リフレッシュトークン再保存後の状態:', {
          exists: !!retryCheck,
          length: retryCheck?.length,
          isSame: retryCheck === response.refresh_token
        });
      }
      
      if (response.user) {
        tokenManager.saveUserData(response.user);
      }
      
      // 保存後の状態を確認
      const savedToken = tokenManager.getToken();
      const savedRefreshToken = tokenManager.getRefreshToken();
      
      logger.log('Firebase認証後の保存状態:', {
        hasToken: !!savedToken,
        tokenLength: savedToken?.length,
        hasRefreshToken: !!savedRefreshToken,
        refreshTokenLength: savedRefreshToken?.length
      });
      
      // 少し待機してからレスポンスを返す（ストレージの反映を待つため）
      await new Promise(resolve => setTimeout(resolve, 300));
      
      return response;
    } catch (error) {
      console.error('Firebase authentication failed:', error);
      throw error;
    }
  }
  /** 実行中のリフレッシュ。同時に呼ばれたら同じ結果を共有する（single-flight）。 */
  private refreshInFlight: Promise<AuthResponse | null> | null = null;

  /**
   * アクセストークンをリフレッシュする
   *
   * ページ表示直後は App / PrivateRoute / 並列 API の 401 がそれぞれリフレッシュを呼ぶ。
   * サーバーはリフレッシュトークンを回転させるので、別々に送ると先着以外が
   * 「回転済みのトークン」を送って 401 になり、認証情報を消して /login へ飛ばしていた。
   * 実行中のリフレッシュがあればその結果を待つ。
   * @returns 新しいトークンと認証情報（失敗時は null。例外は投げない）
   */
  refreshToken(): Promise<AuthResponse | null> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.performRefresh().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  private async performRefresh(): Promise<AuthResponse | null> {
    try {
      // トークンマネージャーからリフレッシュトークンを取得
      const refreshToken = tokenManager.getRefreshToken();
      if (!refreshToken) {
        console.warn('refreshToken: No refresh token found');
        return null;
      }
      
      logger.log('refreshToken: Sending token refresh request', { refreshTokenLength: refreshToken.length });
      
      // デバッグ出力：リフレッシュトークンの値（先頭10文字のみ表示）
      logger.log('Refresh token (first 10 chars):', refreshToken.substring(0, 10) + '...');
      
      try {
        // リトライカウンターを設定
        let retryCount = 0;
        const maxRetries = 2;
        let lastError = null;
        
        // リトライループ
        while (retryCount <= maxRetries) {
          try {
            // axiosインスタンスを直接使用して、インターセプターの無限ループを避ける
            
      // リフレッシュAPIを呼び出し（axiosインスタンスを直接使用）
      logger.log(`refreshToken: API call attempt ${retryCount + 1}/${maxRetries + 1}`);
      
      // リクエストデータを詳細にログ出力
      logger.log('リフレッシュリクエスト:', {
        endpoint: 'auth/refresh',
        refreshTokenLength: refreshToken?.length,
        refreshTokenFirstChars: refreshToken?.substring(0, 10) + '...'
      });
      
      const response = await apiRequest.post<AuthResponse>(
        'auth/refresh', 
        { refresh_token: refreshToken },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          _retry: true // リトライフラグを設定
        } as ExtendedAxiosRequestConfig
      );
      
      // レスポンスデータを詳細にログ出力
      // const responseData = response.data; // この行は削除
      
      logger.log('Token refresh 生レスポンス:', JSON.stringify(response, null, 2));
      logger.log('Token refresh response:', {
        success: true,
        hasUser: !!response.user,
        hasToken: !!response.token,
        hasRefreshToken: !!response.refresh_token,
        tokenLength: response.token?.length,
        refreshTokenLength: response.refresh_token?.length
      });
            
            // トークンマネージャーを使用して新しいトークンを保存
            if (response.token) {
              tokenManager.saveToken(response.token);
            } else {
              console.warn('Response does not contain token');
            }
            
            if (response.refresh_token) {
              tokenManager.saveRefreshToken(response.refresh_token);
              // 追加するログ
              const savedRefTokenAfterRefresh = tokenManager.getRefreshToken();
              logger.log('refreshToken: 保存後のリフレッシュトークン状態 (リフレッシュ後):', {
                hasRefreshToken: !!savedRefTokenAfterRefresh,
                refreshTokenLength: savedRefTokenAfterRefresh?.length
              });
            } else {
              console.warn('Response does not contain refresh_token');
            }
            
            if (response.user) {
              tokenManager.saveUserData(response.user);
            }
            
            return response;
          } catch (error: any) {
            lastError = error;
            console.error(`refreshToken: API request attempt ${retryCount + 1} failed:`, error);
            
            // HTTP エラーの詳細をログ
            if (error.response) {
              console.error('API error details:', {
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data
              });
            }
            
            // 特定のエラー（タイムアウトやネットワーク関連）の場合のみリトライ
            if (error.code === 'ECONNABORTED' || 
                !error.response || 
                error.response.status >= 500) {
              retryCount++;
              // 指数バックオフ - リトライ間の待機時間を徐々に増やす
              const waitTime = Math.pow(2, retryCount) * 500; // 500ms, 1s, 2s
              logger.log(`refreshToken: Retrying in ${waitTime}ms...`);
              await new Promise(resolve => setTimeout(resolve, waitTime));
            } else {
              // その他のエラーはリトライしない（認証エラーなど）
              break;
            }
          }
        }
        
        // すべてのリトライが失敗
        throw lastError;
      } catch (error: any) {
        console.error('refreshToken: All refresh attempts failed:', error);

        // 401 Unauthorizedまたは422 Unprocessable Entityの場合、リフレッシュトークンが無効
        if (error && error.response && (error.response.status === 401 || error.response.status === 422)) {
          // 別タブが先に回転させていれば、ストレージには新しいトークンが入っている。
          // 送った値が古かっただけなので、消さずにそちらを使う。
          const latestRefreshToken = tokenManager.getRefreshToken();
          const latestToken = tokenManager.getToken();
          if (latestRefreshToken && latestRefreshToken !== refreshToken && latestToken) {
            logger.log('refreshToken: Refresh token was rotated by another tab, using the stored one');
            return {
              token: latestToken,
              refresh_token: latestRefreshToken,
              user: tokenManager.getUserData(),
            } as AuthResponse;
          }

          console.warn('refreshToken: Refresh token is invalid, clearing all auth data');
          // 無効なリフレッシュトークンをクリア
          tokenManager.clearAllAuthData();
        }
        // ネットワークエラー（status 0）・タイムアウト・5xx ではトークンを残す。
        // 回線が戻れば次の 401 で再びリフレッシュできる。

        throw error;
      }
    } catch (error) {
      console.error('Token refresh process failed:', error);
      // エラーが発生しても、リフレッシュトークンが存在する場合は削除しない
      return null;
    }
  }
}

export default new AuthService();
